<template>
  <v-container fluid>
    <PageTitle>
      Upload Contacts
      <template v-slot:buttons>
        <v-btn href="https://app.mobilelocker.com/shared/how-to-upload-your-contacts"
               target="_blank"
               small
               class="mr-2">
          <v-icon left>
            fal fa-film
          </v-icon>
          Watch video
        </v-btn>
        <v-btn href="https://teams.mobilelocker.com/demo_team/assets/Mobile%20Locker%20User%20Contact%20List%20Template.xlsx"
               download="Mobile Locker User Contact List Template.xlsx"
               small>
          <v-icon left>
            fal fa-file-excel
          </v-icon>
          Download the template
        </v-btn>
      </template>
    </PageTitle>

    <v-form @submit.prevent="submit">
      <v-card class="mb-4">
        <div class="pa-4">
          <div>
            Fill in the spreadsheet below to upload your contacts into Mobile Locker.
          </div>
          <ul>
            <li>
              <strong>Email address</strong> - If it already exists in your contact list, the record will be updated.
            </li>
            <li>
              <strong>Greeting type</strong> - Formal is the default. If you are on a first-name basis with a person, select "Personal".
            </li>
            <li>
              <strong>Email list name</strong> - Optionally add the contact to a new or existing
              <router-link :to="{name: `${$root.routePrefix}.mailcoach.lists`}">email list</router-link>
            </li>
            <li>
              Email address, first name, and last name are required. All other fields are optional.
            </li>
          </ul>
        </div>
      </v-card>

      <v-card>
        <div>
          <hot-table ref="hotTable"
                     :settings="hotSettings">
          </hot-table>
        </div>
        <v-alert v-if="!uploading && !uploadMessage && hasManyContacts"
                 type="info"
                 tile
                 class="mb-0">
          Uploading {{ form.contacts.length }} contacts may take a while.
        </v-alert>
        <v-alert v-if="uploadMessage && uploadComplete"
                 type="success"
                 tile
                 class="mb-0">
          {{ uploadMessage }}
        </v-alert>
        <v-alert v-else-if="uploadMessage && !uploadComplete"
                 type="info"
                 icon="fal fa-spin fa-spinner"
                 tile
                 class="mb-0">
          {{ uploadMessage }}
        </v-alert>
        <v-card-actions>
          <v-btn type="submit"
                 :disabled="processing || form.contacts.length === 0"
                 color="primary">
            <template v-if="form.busy">
              <v-icon left>
                fal fa-spin fa-spinner
              </v-icon>
              Uploading
            </template>
            <template v-else>
              Upload
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <v-card v-if="1===2 && results">
      <v-card-title class="pb-0">
        Results
      </v-card-title>
      <v-list>
        <template v-for="(result, row) in results">
          <v-list-item>
            <v-list-item-icon>
              <v-icon v-if="result.error">
                fal fa-fw fa-exclamation-triangle
              </v-icon>
              <v-icon v-else-if="result.created || result.updated"
                      color="success">
                fal fa-fw fa-check-circle
              </v-icon>
              <v-icon v-else>
                fal fa-fw fa-check-circle
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <div class="d-flex">
                <div class="mr-4">
                  Row {{ row + 1 }}
                </div>
                <template v-if="result.output">
                  <div v-if="result.created">
                    {{ result.output.name }} added successfully.
                  </div>
                  <div v-else-if="result.updated">
                    {{ result.output.name }} updated successfully.
                  </div>
                  <div v-else>
                    {{ result.output.name }} was not changed.
                  </div>
                </template>
              </div>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
        </template>
      </v-list>
    </v-card>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {chunk, clone, isEmpty, isString} from 'lodash';
import {HotTable} from '@handsontable/vue';
import 'handsontable/dist/handsontable.full.min.css';
import {registerAllModules} from 'handsontable/registry';
import AdminUserContactsToolbar from './components/AdminUserContactsToolbar';
import validationService from "../../../../ml/services/validationService";
import mailcoachService from "../../services/mailcoachService";
import handsontable from "../../../../handsontable";
import echo from '../../plugins/echo';
import userContactService from '../../services/userContactService';
import axios from '../../services/axiosService';
import PageTitle from '../../components/PageTitle.vue';

// MLD-2868 register Handsontable's modules
registerAllModules();

/**
 * @see https://handsontable.com/docs/cell-validator/#full-featured-example
 * @param value
 * @param callback
 */
const emailValidator = (value, callback) => {
  callback(validationService.isEmailValid(value));
}

const npiValidator = (value, callback) => {
  const pattern = new RegExp('^[0-9]{10}$');
  try {
    const result = pattern.test(value);
    callback(result);
  } catch (e) {
    console.log(`Invalid NPI ${value}`, e.message);
    callback(false);
  }
}

export default {
  components: {
    PageTitle,
    AdminUserContactsToolbar,
    HotTable,
  },
  data() {
    return {
      breadcrumbs: [
        {
          exact: true,
          text: 'Contacts',
          to: {name: 'library.user_contacts.index'},
        },
        {
          text: 'Upload',
        }
      ],
      form: new Form({
        user_id: '',
        contacts: [],
        list: null,
        update_contacts: false,
      }),
      hotInstance: null,
      lists: [],
      results: null,
      greetingTypes: [
        'Formal',
        'Personal',
      ],
      salutations: [
        'Dr.',
        'Mr.',
        'Ms.',
        'Miss',
        'Mrs.',
        'Prof.',
      ],
      processing: false,
      uploading: false,
      uploadComplete: false,
      uploadMessage: '',
    }
  },
  computed: {
    ...mapGetters([
      'isAdmin',
      'isMember',
      'team',
      'user',
      'activeUsers',
    ]),
    hotSettings() {
      return {
        colHeaders: this.colHeaders,
        columns: this.columns,
        comments: {
          displayDelay: 250,
          readOnly: true,
        },
        dataSchema: this.dataSchema,
        afterChange: (changes, source) => { // @see https://handsontable.com/docs/api/hooks/#afterchange
          this.populateContacts();
        },
        rowHeaders: true,
        startRows: 16, // @see https://handsontable.com/docs/api/options/#startrows
        minSpareRows: 2,
        height: '400',
        width: '100%',
        licenseKey: handsontable.LICENSE_KEY,
        renderAllRows: false, // MLD-4278
      };
    },
    colHeaders() { // @see https://handsontable.com/docs/api/options/#colheaders
      return [
        'Email address',
        'First name',
        'Last name',
        'Salutation (optional)',
        'Greeting type (optional)',
        'Email list name (optional)',
      ];
    },
    columns() {
      return [
        {
          type: 'text',
          data: 'email',
          validator: emailValidator,
          allowInvalid: true,
        },
        {
          type: 'text',
          data: 'first_name',
        },
        {
          type: 'text',
          data: 'last_name',
        },
        {
          type: 'autocomplete',
          data: 'salutation',
          source: this.salutations,
          strict: true,
        },
        {
          type: 'autocomplete',
          data: 'greeting_type',
          source: this.greetingTypes,
          strict: true,
        },
        {
          type: 'autocomplete',
          data: 'list_name',
          source: this.lists.map(list => list.name),
          strict: false,
        },
      ];
    },
    dataSchema() { // @see https://handsontable.com/docs/api/options/#dataschema
      return {
        user_email: this.user.email,
        email: null,
        first_name: null,
        last_name: null,
        salutation: null,
        greeting_type: null,
        list_name: null,
      }
    },
    commentsPlugin() {
      if (!this.hotInstance) {
        return null;
      }
      return this.hotInstance.getPlugin('comments');
    },
    hasManyContacts() {
      return this.form.contacts.length > 100;
    },
  },
  created() {
    this.initEcho();
    if (this.team.upload_user_contacts_enabled) {
      this.fetchData();
    } else {
      this.$store.dispatch('setErrorMessage', 'You do not have permission to do that.');
      this.$router.replace({name: `${this.$root.routePrefix}.user_contacts.index`});
    }
  },
  mounted() {
    // MLD-2860 @see https://handsontable.com/docs/vue-vuex-example/#overview
    this.hotInstance = this.$refs.hotTable.hotInstance;
    if (this.commentsPlugin) {
      this.commentsPlugin.setCommentAtCell(0, 4, 'Formal is the default. If the user is on a first-name basis with this contact, select Personal.');
      this.commentsPlugin.setCommentAtCell(0, 7, 'You can add this contact to a private email list for the Mobile Locker user. The list will be created if it doesn\'t exist yet.')
    }
  },
  methods: {
    initEcho() {
      echo.private(this.user.channel) // MLD-4278
        .listen('CRM.UserContactsImported', e => {
          console.log('CRM.UserContactsImported', e);
          this.processing = false;
          this.uploading = false;
          this.uploadComplete = true;
          this.uploadMessage = e.message;
        })
    },
    initForm() {
      if (this.isMember) {
        this.form.user_id = this.user.id;
      }
    },
    changeUser() {
      const form = {
        user_id: this.form.user_id,
      };
      return axios.get(`/api/mailcoach/lists`, {params: form})
        .then(response => {
          this.lists = response.data;
        })
    },
    fetchData() {
      this.initForm();
      mailcoachService.getLists()
        .then(data => {
          this.lists = data;
        })
    },
    _resetForm() {
      this.form.contacts = [];
    },
    populateContacts() {
      if (this.hotInstance) {
        this.form.contacts = this.hotInstance.getSourceData().filter(row => {
          return !!row.email
            && isString(row.user_email)
            && validationService.isEmailValid(row.user_email.trim())
            && isString(row.email)
            && validationService.isEmailValid(row.user_email.trim())
            && !!row.first_name
            && isString(row.first_name)
            && !!row.last_name
            && isString(row.last_name)
        });
      }
    },
    submit() {
      if (this.form.busy) {
        return;
      }
      if (!isEmpty(this.form.contacts)) {
        this.results = null;
        const form = this.form.data();
        this.form.startProcessing();
        this.processing = true;
        this.uploadMessage = null;
        this.uploading = true;
        this.uploadComplete = false;

        // MLD-5569
        form.contacts = form.contacts.filter(c => {
          return validationService.isEmailValid(c.email);
        })

        // MLD-5569 @see https://chat.openai.com/c/59507f1d-863c-49e0-a076-26680d588c3e
        const chunks = chunk(form.contacts, 1000);
        const promises = chunks.map(contacts => {
          const chunkForm = clone(form);
          chunkForm.contacts = contacts;
          return userContactService.upload(chunkForm);
        })

        return Promise.all(promises)
          .then(() => {
            this.results = null;
            this.uploadComplete = true;
            this.form.finishProcessing();
          })
          .catch(err => {
            this.uploadComplete = false;
            this.uploadMessage = err.response?.data?.message;
            console.log(err.response?.data);
          })
          .finally(() => {
            this.form.busy = false;
            this.processing = false;
            this.uploading = false;
          })

        /*return userContactService.upload(form)
          .then(data => {
            if (isArray(data)) {
              this.results = data;
              this.processing = false;
              this.uploading = false;
              this.uploadComplete = true;
            } else if (isObject(data) && isString(data.message)) {
              this.uploadMessage = data.message;
            }
            this.form.finishProcessing();
          })
          .catch(err => {
            console.log(err);
          })
          .finally(() => {
            this.form.busy = false;
          })*/
      }
    }
  }
}
</script>
